import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePublicPetitionWizardContext } from '../../../hooks/useCaseRequestWizard';
import type { PublicPetitionWizardDraft } from '../../../types/publicPetitionCaseRequest';
import type { RequiredErrors } from '../../../utils/requiredValidation';
import { WIZARD_BASE, isWizardCategorizationEditable, wizardReviewPath } from '../../../utils/wizardCategoryRoute';
import { StepBar, StepSep, WizardStep } from '../WizardComponents';
import { wizardStepBarLabel, wizardStepTitle } from '../wizardI18nHelpers';
import { PublicPetitionCaseCategorizationCard } from './PublicPetitionCaseCategorizationCard';
import { PUBLIC_PETITION_WIZARD_STEPS, type PublicPetitionWizardStep } from './publicPetitionConstants';
import { useRole } from '../../../rbac/hooks';
import { tasksListPathForRole } from '../../../rbac/tasksListPath';
export function publicPetitionWizardPath(step: PublicPetitionWizardStep, caseRequestId?: string): string {
  const base = `${WIZARD_BASE}/step-${String(step)}`;
  const id = (caseRequestId ?? '').trim();
  return id ? `${base}/${id}` : base;
}

export function publicPetitionReviewPath(caseRequestId?: string): string {
  return wizardReviewPath(undefined, caseRequestId);
}

export function PublicPetitionWizardShell({
  currentStep,
  draft,
  setDraft,
  categorizationEditable = true,
  categorizationErrors,
  onClearCategorizationError,
  children,
}: {
  currentStep: PublicPetitionWizardStep;
  draft: PublicPetitionWizardDraft;
  setDraft?: React.Dispatch<React.SetStateAction<PublicPetitionWizardDraft>>;
  categorizationEditable?: boolean;
  categorizationErrors?: RequiredErrors;
  onClearCategorizationError?: (key: string) => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { roleCode } = useRole();
  const { setDraft: contextSetDraft } = usePublicPetitionWizardContext();
  const effectiveSetDraft = setDraft ?? contextSetDraft;
  const showWizard = Boolean(draft.fileCategoryId);

  return (
    <>
      <div className="bc">
        <Link to={tasksListPathForRole(roleCode)}>{t('nav.tasksAndCases')}</Link>
        <span className="bc-sep">/</span>
        {t('wizard.createCaseRequest')}
      </div>

      <div className="pg-title">{t('wizard.createCaseRequest')}</div>
      <div className="pg-sub" style={{ marginBottom: 16 }}>
        {t('wizard.createSubtitle')}
      </div>

      <PublicPetitionCaseCategorizationCard
        draft={draft}
        setDraft={effectiveSetDraft}
        editable={isWizardCategorizationEditable(categorizationEditable, draft.draftRequestId)}
        errors={categorizationErrors}
        onClearError={onClearCategorizationError}
      />

      {showWizard ? (
        <>
          <StepBar>
            {PUBLIC_PETITION_WIZARD_STEPS.map((s, idx) => {
              const state = currentStep > s.n ? 'done' : currentStep === s.n ? 'active' : 'pending';
              return (
                <div key={s.n} style={{ display: 'flex', alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
                  <WizardStep
                    state={state}
                    numberOrCheck={state === 'done' ? '✓' : String(s.n)}
                    stepLabel={wizardStepBarLabel(t, s.n)}
                    name={wizardStepTitle(t, 'publicPetition', s.n)}
                    clickable={false}
                  />
                  {idx < PUBLIC_PETITION_WIZARD_STEPS.length - 1 ? <StepSep /> : null}
                </div>
              );
            })}
          </StepBar>

          {children}
        </>
      ) : null}
    </>
  );
}

export function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }} role="alert">
      {message}
    </div>
  );
}

export function inpClass(hasError: boolean): string {
  return `ccr-inp${hasError ? ' inv' : ''}`;
}
